import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPlus, faEdit, faTrash, faUsers, faCalendarDays,
  faLocationDot, faSpinner, faExclamationTriangle, faEye,
  faDownload, faSearch, faChartLine, faTimes, faSave, faImage,
  faGlobe, faInfoCircle, faDollarSign, faMapMarkerAlt, faClock, faGear, faRoute
} from '@fortawesome/free-solid-svg-icons';
import { eventService, registrationService } from '../services/database';
import { storageService } from '../services/storageService';
import { authService } from '../services/authService';
import { CoordinatorUpgradeService } from '../services/coordinatorUpgradeService';
import { Event, Registration, EventDistance } from '../lib/supabase';

interface EventWithStats extends Event {
  registrationCount: number
  registrationRate: number
  recentRegistrations: Registration[]
}

interface DistanceOption {
  id: string
  name: string
  length: string
  price: string
  description: string
  ageRestriction: string
  difficulty: string
}

interface CreateEventForm {
  title: string
  description: string
  date: string
  location: string
  max_participants: number
  registration_deadline: string
  requirements: string
  amenities: string[]
  organizer: string
  difficulty: string
  entry_fee_from: string
  image_url: string
  route_url: string
  goreg: 'yes' | 'no'
  distances: DistanceOption[]
}

const CoordinatorEventManagement: React.FC = () => {
  const [events, setEvents] = useState<EventWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventWithStats | null>(null);
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState<Registration[]>([]);
  const [distances, setDistances] = useState<EventDistance[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'upcoming' | 'past'>('all');
  
  // Event creation state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  
  // Event editing state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventWithStats | null>(null);
  const [editForm, setEditForm] = useState<CreateEventForm>({
    title: '',
    description: '',
    date: '',
    location: '',
    max_participants: 100,
    registration_deadline: '',
    requirements: '',
    amenities: [],
    organizer: '',
    difficulty: 'Intermediate',
    entry_fee_from: '',
    image_url: '',
    route_url: '',
    goreg: 'yes',
    distances: [
      {
        id: 'distance-1',
        name: 'Standard Course',
        length: '25 km',
        price: '250',
        description: 'Main trail route',
        ageRestriction: '12+',
        difficulty: 'Intermediate'
      }
    ]
  });
  const [createForm, setCreateForm] = useState<CreateEventForm>({
    title: '',
    description: '',
    date: '',
    location: '',
    max_participants: 100,
    registration_deadline: '',
    requirements: '',
    amenities: [],
    organizer: '',
    difficulty: 'Intermediate',
    entry_fee_from: '',
    image_url: '',
    route_url: '',
    goreg: 'yes',
    distances: [
      {
        id: 'distance-1',
        name: 'Standard Course',
        length: '25 km',
        price: '250',
        description: 'Main trail route',
        ageRestriction: '12+',
        difficulty: 'Intermediate'
      }
    ]
  });

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const hasAccess = await authService.canAccessCoordinatorFeatures();
      if (!hasAccess) {
        setError('Access denied: Coordinator privileges required');
        setLoading(false);
        return;
      }
      loadEvents();
    } catch (err) {
      console.error('Error checking access:', err);
      setError('Failed to verify access permissions');
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current coordinator ID
      const coordinatorId = await authService.getCurrentCoordinatorId();
      console.log('Coordinator ID:', coordinatorId);
      
      if (!coordinatorId) {
        setError('Access denied: Coordinator authentication required');
        setEvents([]);
        return;
      }

      // Get events created by this coordinator only
      console.log('Fetching events for coordinator:', coordinatorId);
      const coordinatorEvents = await eventService.getEventsByCoordinator(coordinatorId);
      console.log('Found events:', coordinatorEvents);
      
      // If no events found, that's normal for new coordinators - don't show error
      if (!coordinatorEvents || coordinatorEvents.length === 0) {
        console.log('No events found for coordinator - this is normal for new coordinators');
        setEvents([]);
        return;
      }
      
      // Get registration stats for each event
      const eventsWithStats = await Promise.all(
        coordinatorEvents.map(async (event) => {
          try {
            const registrations = await registrationService.getEventRegistrations(event.id);
            const registrationCount = registrations.length;
            const registrationRate = event.max_participants > 0 
              ? Math.round((registrationCount / event.max_participants) * 100)
              : 0;

            return {
              ...event,
              registrationCount,
              registrationRate,
              recentRegistrations: registrations.slice(0, 5) // Last 5 registrations
            };
          } catch (err) {
            console.error(`Error loading stats for event ${event.id}:`, err);
            return {
              ...event,
              registrationCount: 0,
              registrationRate: 0,
              recentRegistrations: []
            };
          }
        })
      );

      setEvents(eventsWithStats);
    } catch (err) {
      console.error('Error loading events:', err);
      
      // Type guard for error object
      const error = err as Error;
      console.error('Error details:', error.message, (error as any).code);
      
      // Only show error if it's not a "no events found" situation
      if (error.message && !error.message.includes('No rows')) {
        setError(`Failed to load events: ${error.message}`);
      } else {
        // No events found is normal for new coordinators
        setEvents([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEventParticipants = async (eventId: string) => {
    try {
      const [registrations, eventDistances] = await Promise.all([
        registrationService.getEventRegistrations(eventId),
        eventService.getEventDistances(eventId)
      ]);
      setParticipants(registrations);
      setDistances(eventDistances);
      setShowParticipants(true);
    } catch (err) {
      console.error('Error loading participants:', err);
      setError('Failed to load participants');
    }
  };

  const exportParticipantsToCSV = (eventTitle: string, participantsList: Registration[], distancesList: EventDistance[]) => {
    try {
      // Create CSV headers
      const headers = [
        'Name',
        'Participant Type', 
        'Email', 
        'Phone',
        'Emergency Contact',
        'Medical Conditions',
        'Distance',
        'Status',
        'Payment Status',
        'Payment Method',
        'Payment Amount',
        'Registration Date'
      ];

      // Create CSV rows
      const rows = participantsList.map(participant => {
        const distance = distancesList.find(d => d.id === participant.distance_id);
        
        // Determine if this is likely an account holder or a minor based on available data
        let participantType = 'Unknown';
        
        // Check if we can determine participant type from email and name
        const emailParts = participant.user_email?.split('@') || [];
        const emailUsername = emailParts[0] || '';
        const nameWords = (participant.user_name || '').toLowerCase().split(' ');
        
        // If email username contains part of the name, likely account holder
        const isEmailRelatedToName = nameWords.some(word => 
          word.length > 3 && emailUsername.toLowerCase().includes(word.toLowerCase())
        );
        
        if (isEmailRelatedToName) {
          participantType = 'Account Holder';
        } else {
          participantType = 'Minor/Dependent';
        }
        
        return [
          participant.user_name || '',
          participantType,
          participant.user_email || '',
          participant.user_phone || '',
          participant.emergency_contact || '',
          participant.medical_conditions || '',
          distance ? `${distance.name} (${distance.length})` : '',
          participant.status || '',
          participant.payment_status || '',
          participant.payment_method || '',
          participant.payment_amount ? `R${participant.payment_amount.toFixed(2)}` : '',
          participant.registration_date ? new Date(participant.registration_date).toLocaleDateString() : ''
        ];
      });

      // Combine headers and rows
      const csvContent = [headers, ...rows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${eventTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_participants.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported ${participantsList.length} participants to CSV`);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      setError('Failed to export participant list');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getEventStatus = (event: Event) => {
    const now = new Date();
    const eventDate = new Date(event.date);
    const registrationDeadline = new Date(event.registration_deadline);

    if (eventDate < now) return { status: 'past', color: 'bg-gray-100 text-gray-800' };
    if (registrationDeadline < now) return { status: 'closed', color: 'bg-red-100 text-red-800' };
    return { status: 'open', color: 'bg-green-100 text-green-800' };
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    const now = new Date();
    const eventDate = new Date(event.date);

    switch (filterStatus) {
      case 'upcoming':
        return eventDate > now;
      case 'past':
        return eventDate < now;
      default:
        return true;
    }
  });

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingEvent(null);
    setImageFile(null);
    setImagePreview('');
    setError(null);
    // Reset form to initial state
    setCreateForm({
      title: '',
      description: '',
      date: '',
      location: '',
      max_participants: 100,
      registration_deadline: '',
      requirements: '',
      amenities: [],
      organizer: '',
      difficulty: 'Intermediate',
      entry_fee_from: '',
      image_url: '',
      route_url: '',
      goreg: 'yes',
      distances: [
        {
          id: 'distance-1',
          name: 'Standard Course',
          length: '25 km',
          price: '250',
          description: 'Main trail route',
          ageRestriction: '12+',
          difficulty: 'Intermediate'
        }
      ]
    });
    setEditForm({
      title: '',
      description: '',
      date: '',
      location: '',
      max_participants: 100,
      registration_deadline: '',
      requirements: '',
      amenities: [],
      organizer: '',
      difficulty: 'Intermediate',
      entry_fee_from: '',
      image_url: '',
      route_url: '',
      goreg: 'yes',
      distances: [
        {
          id: 'distance-1',
          name: 'Standard Course',
          length: '25 km',
          price: '250',
          description: 'Main trail route',
          ageRestriction: '12+',
          difficulty: 'Intermediate'
        }
      ]
    });
  };

  const handleCreateEvent = async () => {
    try {
      setCreateLoading(true);
      setError(null);

      // Get the current user
      const currentUser = await authService.getCurrentUser();
      if (!currentUser) {
        setError('User not logged in');
        return;
      }
      const isAdmin = currentUser.role === 'admin';
      const coordinatorId = await authService.getCurrentCoordinatorId();
      if (!coordinatorId) {
        setError('Access denied: Coordinator authentication required');
        return;
      }
      const coordinatorEmail = currentUser.email;

      // Check if coordinator has available credits (skip for admin)
      if (!isAdmin) {
        const creditCheck = await CoordinatorUpgradeService.canCreateEvent(coordinatorEmail);
        if (!creditCheck.canCreate) {
          if (!creditCheck.isCoordinator) {
            setError('You must be a coordinator to create events. Please upgrade your account first.');
          } else {
            setError('You need event creation credits to create new events. Please purchase credits to continue.');
          }
          return;
        }
      }

      // Validate required fields
      if (!createForm.title || !createForm.description || !createForm.date || 
          !createForm.location || !createForm.registration_deadline) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate dates
      const eventDate = new Date(createForm.date);
      const deadlineDate = new Date(createForm.registration_deadline);
      const now = new Date();

      if (eventDate <= now) {
        setError('Event date must be in the future');
        return;
      }

      if (deadlineDate >= eventDate) {
        setError('Registration deadline must be before the event date');
        return;
      }

      // Handle image upload
      let imageUrl = createForm.image_url;
      if (imageFile) {
        try {
          // Upload image to Supabase Storage
          imageUrl = await storageService.uploadEventImage(imageFile);
          console.log('Image uploaded successfully:', imageUrl);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          setError(
            uploadError instanceof Error
              ? uploadError.message
              : 'Failed to upload image. Please try again.'
          );
          return;
        }
      }

      // Create the event with coordinator ID (exclude distances from event data)
      const { distances, ...eventData } = createForm;
      const newEvent = await eventService.createEvent({
        ...eventData,
        organizer_id: coordinatorId,
        image_url: imageUrl,
        current_participants: 0,
        amenities: createForm.amenities.length > 0 ? createForm.amenities : undefined
      });

      // Create event distances
      if (createForm.distances && createForm.distances.length > 0) {
        const distanceData = createForm.distances.map(distance => ({
          name: distance.name,
          length: distance.length,
          price: parseFloat(distance.price) || 0,
          description: distance.description,
          age_restriction: distance.ageRestriction,
          difficulty: distance.difficulty
        }));
        await eventService.createEventDistances(newEvent.id, distanceData);
      }

      // Use one event creation credit (skip for admin)
      if (!isAdmin) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const creditUsed = await CoordinatorUpgradeService.useEventCredit(coordinatorEmail);
        if (!creditUsed) {
          console.warn('Failed to use event credit, but event was created successfully');
          // Don't fail the event creation if credit usage fails
        }
      }

      // Reload events to show the new one
      await loadEvents();

      // Reset form and close modal
      setCreateForm({
        title: '',
        description: '',
        date: '',
        location: '',
        max_participants: 100,
        registration_deadline: '',
        requirements: '',
        amenities: [],
        organizer: '',
        difficulty: 'Intermediate',
        entry_fee_from: '',
        image_url: '',
        route_url: '',
        goreg: 'yes',
        distances: [
          {
            id: 'distance-1',
            name: 'Standard Course',
            length: '25 km',
            price: '250',
            description: 'Main trail route',
            ageRestriction: '12+',
            difficulty: 'Intermediate'
          }
        ]
      });
      setImageFile(null);
      setImagePreview('');
      setShowCreateModal(false);

      console.log('Event created successfully:', newEvent);
    } catch (err) {
      console.error('Error creating event:', err);
      setError('Failed to create event. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAmenityToggle = (amenity: string) => {
    setCreateForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleEditAmenityToggle = (amenity: string) => {
    setEditForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const initializeEditForm = async (event: EventWithStats) => {
    console.log('Initializing edit form with event:', event);
    
    // Convert date to the correct format for input[type="datetime-local"]
    const eventDate = new Date(event.date);
    const formattedDate = eventDate.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm format

    const deadlineDate = new Date(event.registration_deadline);
    const formattedDeadline = deadlineDate.toISOString().slice(0, 16);

    // Load actual distances from the database
    let eventDistances: DistanceOption[] = [];
    try {
      const distances = await eventService.getEventDistances(event.id);
      eventDistances = distances.map(d => ({
        id: d.id,
        name: d.name,
        length: d.length,
        price: d.price.toString(),
        description: d.description || '',
        ageRestriction: d.age_restriction || '12+',
        difficulty: d.difficulty || 'Intermediate'
      }));
    } catch (err) {
      console.error('Error loading event distances:', err);
      // Use default distance if loading fails
      eventDistances = [
        {
          id: 'distance-1',
          name: 'Standard Course',
          length: '25 km',
          price: '250',
          description: 'Main trail route',
          ageRestriction: '12+',
          difficulty: 'Intermediate'
        }
      ];
    }

    setEditForm({
      title: event.title || '',
      description: event.description || '',
      date: formattedDate,
      location: event.location || '',
      max_participants: event.max_participants || 100,
      registration_deadline: formattedDeadline,
      requirements: event.requirements || '',
      amenities: event.amenities || [],
      organizer: event.organizer || '',
      difficulty: event.difficulty || 'Intermediate',
      entry_fee_from: event.entry_fee_from || '',
      image_url: event.image_url || '',
      route_url: event.route_url || '',
      goreg: event.goreg || 'yes',
      distances: eventDistances
    });
    
    setEditingEvent(event);
    setShowEditModal(true);
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;

    try {
      setCreateLoading(true);
      setError(null);

      // Validate required fields
      if (!editForm.title || !editForm.description || !editForm.date || 
          !editForm.location || !editForm.registration_deadline) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate dates
      const eventDate = new Date(editForm.date);
      const deadlineDate = new Date(editForm.registration_deadline);
      const now = new Date();

      if (eventDate <= now) {
        setError('Event date must be in the future');
        return;
      }

      if (deadlineDate >= eventDate) {
        setError('Registration deadline must be before the event date');
        return;
      }

      // Handle image upload if a new image was selected
      let imageUrl = editForm.image_url;
      if (imageFile) {
        try {
          imageUrl = await storageService.uploadEventImage(imageFile);
          console.log('Image uploaded successfully:', imageUrl);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          setError(
            uploadError instanceof Error
              ? uploadError.message
              : 'Failed to upload image. Please try again.'
          );
          return;
        }
      }

      // Update the event (exclude distances from event data)
      const { distances, ...eventData } = editForm;
      const updatedEvent = await eventService.updateEvent(editingEvent.id, {
        ...eventData,
        image_url: imageUrl,
        amenities: editForm.amenities.length > 0 ? editForm.amenities : undefined
      });

      // Update event distances
      if (editForm.distances && editForm.distances.length > 0) {
        const distanceData = editForm.distances.map(distance => ({
          name: distance.name,
          length: distance.length,
          price: parseFloat(distance.price) || 0,
          description: distance.description,
          age_restriction: distance.ageRestriction,
          difficulty: distance.difficulty
        }));
        
        await eventService.updateEventDistances(editingEvent.id, distanceData);
      }

      // Reload events to show the updated one
      await loadEvents();

      // Close modal and reset
      handleCloseModal();

      console.log('Event updated successfully:', updatedEvent);
    } catch (err) {
      console.error('Error updating event:', err);
      setError('Failed to update event. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  const commonAmenities = [
    'Parking', 'Toilets', 'Water Points', 'Medical Support', 'Bike Wash',
    'Food Vendors', 'Timing Chips', 'Photography', 'Live Tracking', 'Prizes'
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin mb-4" />
        <p className="text-gray-600">Loading events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-4">Error Loading Events</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button 
          onClick={loadEvents}
          className="bg-primary text-white px-6 py-2 rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 font-sans text-dark">
      {/* Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
          </Link>
          <h1 className="app-header-title">Event Management</h1>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-primary text-white px-3 py-1.5 rounded-lg text-sm hover:bg-orange-600 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Create Event
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="content-container pb-20">
        {/* Statistics Overview */}
        <section className="bg-white p-4 mb-3">
          <h3 className="font-semibold text-gray-800 mb-3">Overview</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">{events.length}</p>
              <p className="text-xs text-gray-600">Total Events</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {events.reduce((sum, event) => sum + event.registrationCount, 0)}
              </p>
              <p className="text-xs text-gray-600">Total Registrations</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">
                {events.filter(event => new Date(event.date) > new Date()).length}
              </p>
              <p className="text-xs text-gray-600">Upcoming Events</p>
            </div>
          </div>
        </section>

        {/* Search and Filter */}
        <section className="bg-white p-4 mb-3">
          <div className="flex space-x-3 mb-3">
            <div className="flex-1 relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
            >
              <option value="all">All Events</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past Events</option>
            </select>
          </div>
        </section>

        {/* Events List */}
        <section className="space-y-3">
          {filteredEvents.length > 0 ? (
            filteredEvents.map((event) => {
              const eventStatus = getEventStatus(event);
              return (
                <div key={event.id} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="font-semibold text-lg mr-2">{event.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${eventStatus.color}`}>
                          {eventStatus.status}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faCalendarDays} className="mr-2 w-4" />
                          <span>{formatDate(event.date)} • {formatTime(event.date)}</span>
                        </div>
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faLocationDot} className="mr-2 w-4" />
                          <span>{event.location}</span>
                        </div>
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faUsers} className="mr-2 w-4" />
                          <span>{event.registrationCount}/{event.max_participants} registered ({event.registrationRate}%)</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-1 ml-4">
                      <Link 
                        to={`/events/${event.id}`}
                        className="p-2 text-gray-500 hover:text-primary"
                        title="View Event"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Link>
                      <button 
                        onClick={() => {
                          console.log('Edit event clicked:', event.id);
                          initializeEditForm(event);
                        }}
                        className="p-2 text-gray-500 hover:text-primary"
                        title="Edit Event"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedEvent(event);
                          loadEventParticipants(event.id);
                        }}
                        className="p-2 text-gray-500 hover:text-primary"
                        title="View Participants"
                      >
                        <FontAwesomeIcon icon={faUsers} />
                      </button>
                      <button 
                        className="p-2 text-gray-500 hover:text-red-500"
                        title="Delete Event"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>

                  {/* Registration Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${Math.min(event.registrationRate, 100)}%` }}
                    ></div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-lg font-bold text-primary">{event.registrationCount}</p>
                      <p className="text-xs text-gray-600">Registered</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">{event.registrationRate}%</p>
                      <p className="text-xs text-gray-600">Capacity</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-orange-600">{event.recentRegistrations.length}</p>
                      <p className="text-xs text-gray-600">Recent</p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white rounded-lg p-8 text-center">
              <FontAwesomeIcon icon={faCalendarDays} className="text-4xl text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Events Found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterStatus !== 'all' 
                  ? 'No events match your search criteria.' 
                  : 'You haven\'t created any events yet. Create your first event to start managing mountain biking events.'}
              </p>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Create Your First Event
              </button>
            </div>
          )}
        </section>
      </main>

      {/* Participants Modal */}
      {showParticipants && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Participants - {selectedEvent.title}</h3>
                <button 
                  onClick={() => setShowParticipants(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {participants.length} registered participants
              </p>
            </div>
            <div className="p-4 overflow-y-auto max-h-96">
              {participants.length > 0 ? (
                <div className="space-y-3">
                  {participants.map((participant, index) => (
                    <div key={participant.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{participant.user_name}</h4>
                          <p className="text-sm text-gray-600">{participant.user_email}</p>
                          <p className="text-xs text-gray-500">
                            Registered: {formatDate(participant.registration_date)}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          participant.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          participant.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {participant.status}
                        </span>
                      </div>
                      {participant.distance_id && (
                        <p className="text-sm text-primary mt-1">
                          Distance: {distances.find(d => d.id === participant.distance_id)?.name || participant.distance_id}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faUsers} className="text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500">No participants yet</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t">
              <button 
                onClick={() => exportParticipantsToCSV(selectedEvent?.title || 'Event', participants, distances)}
                className="w-full bg-primary text-white py-2 rounded-lg hover:bg-orange-600 transition-colors"
                disabled={participants.length === 0}
              >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Export Participant List ({participants.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Create New Event</h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Fill in all the details to create a new mountain biking event
              </p>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Basic Information */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-primary" />
                    Basic Information
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Event Title *
                      </label>
                      <input
                        type="text"
                        value={createForm.title}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Event title"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <textarea
                        value={createForm.description}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe your event, what makes it special, and what participants can expect..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Event Date *
                        </label>
                        <input
                          type="datetime-local"
                          value={createForm.date}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, date: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Registration Deadline *
                        </label>
                        <input
                          type="datetime-local"
                          value={createForm.registration_deadline}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, registration_deadline: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location *
                      </label>
                      <input
                        type="text"
                        value={createForm.location}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="Event location"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Event Details */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faGear} className="mr-2 text-primary" />
                    Event Details
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Participants
                        </label>
                        <input
                          type="number"
                          value={createForm.max_participants}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, max_participants: parseInt(e.target.value) || 0 }))}
                          min="1"
                          max="1000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Difficulty Level
                        </label>
                        <select
                          value={createForm.difficulty}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, difficulty: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        >
                          <option value="Beginner">Beginner</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Advanced">Advanced</option>
                          <option value="Expert">Expert</option>
                          <option value="All Levels">All Levels</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Entry Fee From
                        </label>
                        <input
                          type="text"
                          value={createForm.entry_fee_from}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, entry_fee_from: e.target.value }))}
                          placeholder="Entry fee amount"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Organizer
                        </label>
                        <input
                          type="text"
                          value={createForm.organizer}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, organizer: e.target.value }))}
                          placeholder="Organizer name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Requirements
                      </label>
                      <textarea
                        value={createForm.requirements}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, requirements: e.target.value }))}
                        placeholder="List event requirements and safety regulations"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                </div>

                {/* Media & Links */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faImage} className="mr-2 text-primary" />
                    Media & Links
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Event Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            const file = e.target.files[0];
                            
                            // Validate the file
                            const validation = storageService.validateImageFile(file);
                            if (!validation.isValid) {
                              setError(validation.error || 'Invalid image file');
                              return;
                            }
                            
                            setImageFile(file);
                            setImagePreview(URL.createObjectURL(file));
                            setError(null); // Clear any previous errors
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Upload JPEG, PNG, or WebP images. Maximum size: 5MB
                      </p>
                      {imagePreview && (
                        <div className="mt-2">
                          <img 
                            src={imagePreview} 
                            alt="Event preview" 
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview('');
                            }}
                            className="mt-1 text-sm text-red-600 hover:text-red-800"
                          >
                            Remove image
                          </button>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Route URL
                      </label>
                      <input
                        type="url"
                        value={createForm.route_url}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, route_url: e.target.value }))}
                        placeholder="https://your-domain.com/route-map"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                </div>

                {/* Distance Options */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faRoute} className="mr-2 text-primary" />
                    Distance Options
                  </h4>
                  
                  <div className="space-y-3">
                    {createForm.distances?.map((distance, index) => (
                      <div key={distance.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Distance Name *
                            </label>
                            <input
                              type="text"
                              value={distance.name}
                              onChange={(e) => {
                                const newDistances = [...(createForm.distances || [])];
                                newDistances[index] = { ...distance, name: e.target.value };
                                setCreateForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              placeholder="e.g., Long Course"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Length *
                            </label>
                            <input
                              type="text"
                              value={distance.length}
                              onChange={(e) => {
                                const newDistances = [...(createForm.distances || [])];
                                newDistances[index] = { ...distance, length: e.target.value };
                                setCreateForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              placeholder="e.g., 25 km"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Price (R) *
                            </label>
                            <input
                              type="number"
                              value={distance.price}
                              onChange={(e) => {
                                const newDistances = [...(createForm.distances || [])];
                                newDistances[index] = { ...distance, price: e.target.value };
                                setCreateForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              placeholder="250"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Age Restriction
                            </label>
                            <select
                              value={distance.ageRestriction}
                              onChange={(e) => {
                                const newDistances = [...(createForm.distances || [])];
                                newDistances[index] = { ...distance, ageRestriction: e.target.value };
                                setCreateForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            >
                              <option value="All Ages">All Ages</option>
                              <option value="12+">12+</option>
                              <option value="16+">16+</option>
                              <option value="18+">18+ (Adults Only)</option>
                              <option value="Under 18">Under 18 (Juniors)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Difficulty
                            </label>
                            <select
                              value={distance.difficulty}
                              onChange={(e) => {
                                const newDistances = [...(createForm.distances || [])];
                                newDistances[index] = { ...distance, difficulty: e.target.value };
                                setCreateForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            >
                              <option value="Beginner">Beginner</option>
                              <option value="Intermediate">Intermediate</option>
                              <option value="Advanced">Advanced</option>
                              <option value="Expert">Expert</option>
                            </select>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            value={distance.description}
                            onChange={(e) => {
                              const newDistances = [...(createForm.distances || [])];
                              newDistances[index] = { ...distance, description: e.target.value };
                              setCreateForm(prev => ({ ...prev, distances: newDistances }));
                            }}
                            placeholder="Brief description of this distance option"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                          />
                        </div>
                        {(createForm.distances?.length || 0) > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newDistances = createForm.distances?.filter((_, i) => i !== index) || [];
                              setCreateForm(prev => ({ ...prev, distances: newDistances }));
                            }}
                            className="text-red-600 text-xs hover:text-red-800"
                          >
                            Remove this distance option
                          </button>
                        )}
                      </div>
                    )) || []}
                    
                    <button
                      type="button"
                      onClick={() => {
                        const newDistance = {
                          id: `distance-${Date.now()}`,
                          name: '',
                          length: '',
                          price: '',
                          description: '',
                          ageRestriction: 'All Ages',
                          difficulty: 'Intermediate'
                        };
                        setCreateForm(prev => ({ 
                          ...prev, 
                          distances: [...(prev.distances || []), newDistance] 
                        }));
                      }}
                      className="w-full py-2 px-3 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors text-sm"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Add Another Distance Option
                    </button>
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-primary" />
                    Amenities
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {commonAmenities.map((amenity) => (
                      <label key={amenity} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={createForm.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                          className="mr-2 text-primary focus:ring-primary"
                        />
                        <span className="text-sm">{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50">
              <div className="flex space-x-3">
                <button 
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={createLoading}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleCreateEvent}
                  disabled={createLoading}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Create Event
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Edit Event</h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Update the details for "{editingEvent.title}"
              </p>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Basic Information */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-primary" />
                    Basic Information
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Event Title *
                      </label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Event title"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe your event, what makes it special, and what participants can expect..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Event Date & Time *
                        </label>
                        <input
                          type="datetime-local"
                          value={editForm.date}
                          onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Registration Deadline *
                        </label>
                        <input
                          type="datetime-local"
                          value={editForm.registration_deadline}
                          onChange={(e) => setEditForm(prev => ({ ...prev, registration_deadline: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location *
                      </label>
                      <input
                        type="text"
                        value={editForm.location}
                        onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="Event location"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Participants
                        </label>
                        <input
                          type="number"
                          value={editForm.max_participants}
                          onChange={(e) => setEditForm(prev => ({ ...prev, max_participants: parseInt(e.target.value) || 0 }))}
                          min="1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Difficulty Level
                        </label>
                        <select
                          value={editForm.difficulty}
                          onChange={(e) => setEditForm(prev => ({ ...prev, difficulty: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        >
                          <option value="Beginner">Beginner</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Advanced">Advanced</option>
                          <option value="Expert">Expert</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Entry Fee From
                        </label>
                        <input
                          type="text"
                          value={editForm.entry_fee_from}
                          onChange={(e) => setEditForm(prev => ({ ...prev, entry_fee_from: e.target.value }))}
                          placeholder="Entry fee amount"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Organizer
                        </label>
                        <input
                          type="text"
                          value={editForm.organizer}
                          onChange={(e) => setEditForm(prev => ({ ...prev, organizer: e.target.value }))}
                          placeholder="Organizer name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Requirements
                      </label>
                      <textarea
                        value={editForm.requirements}
                        onChange={(e) => setEditForm(prev => ({ ...prev, requirements: e.target.value }))}
                        placeholder="List event requirements and safety regulations"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                </div>

                {/* Media & Links */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faImage} className="mr-2 text-primary" />
                    Media & Links
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Event Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            const file = e.target.files[0];
                            
                            // Validate the file
                            const validation = storageService.validateImageFile(file);
                            if (!validation.isValid) {
                              setError(validation.error || 'Invalid image file');
                              return;
                            }
                            
                            setImageFile(file);
                            setImagePreview(URL.createObjectURL(file));
                            setError(null); // Clear any previous errors
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Upload JPEG, PNG, or WebP images. Maximum size: 5MB
                      </p>
                      {(imagePreview || editForm.image_url) && (
                        <div className="mt-2">
                          <img 
                            src={imagePreview || editForm.image_url} 
                            alt="Event preview" 
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview('');
                              if (!imagePreview) {
                                setEditForm(prev => ({ ...prev, image_url: '' }));
                              }
                            }}
                            className="mt-1 text-sm text-red-600 hover:text-red-800"
                          >
                            Remove image
                          </button>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Route URL
                      </label>
                      <input
                        type="url"
                        value={editForm.route_url}
                        onChange={(e) => setEditForm(prev => ({ ...prev, route_url: e.target.value }))}
                        placeholder="https://your-domain.com/route-map"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                </div>

                {/* Distance Options */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faRoute} className="mr-2 text-primary" />
                    Distance Options
                  </h4>
                  
                  <div className="space-y-3">
                    {editForm.distances?.map((distance, index) => (
                      <div key={distance.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Distance Name *
                            </label>
                            <input
                              type="text"
                              value={distance.name}
                              onChange={(e) => {
                                const newDistances = [...(editForm.distances || [])];
                                newDistances[index] = { ...distance, name: e.target.value };
                                setEditForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              placeholder="e.g., Long Course"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Length *
                            </label>
                            <input
                              type="text"
                              value={distance.length}
                              onChange={(e) => {
                                const newDistances = [...(editForm.distances || [])];
                                newDistances[index] = { ...distance, length: e.target.value };
                                setEditForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              placeholder="e.g., 25 km"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Price (R) *
                            </label>
                            <input
                              type="number"
                              value={distance.price}
                              onChange={(e) => {
                                const newDistances = [...(editForm.distances || [])];
                                newDistances[index] = { ...distance, price: e.target.value };
                                setEditForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              placeholder="250"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Age Restriction
                            </label>
                            <input
                              type="text"
                              value={distance.ageRestriction}
                              onChange={(e) => {
                                const newDistances = [...(editForm.distances || [])];
                                newDistances[index] = { ...distance, ageRestriction: e.target.value };
                                setEditForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              placeholder="12+"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Difficulty
                            </label>
                            <select
                              value={distance.difficulty}
                              onChange={(e) => {
                                const newDistances = [...(editForm.distances || [])];
                                newDistances[index] = { ...distance, difficulty: e.target.value };
                                setEditForm(prev => ({ ...prev, distances: newDistances }));
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                            >
                              <option value="Beginner">Beginner</option>
                              <option value="Intermediate">Intermediate</option>
                              <option value="Advanced">Advanced</option>
                              <option value="Expert">Expert</option>
                            </select>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={distance.description}
                            onChange={(e) => {
                              const newDistances = [...(editForm.distances || [])];
                              newDistances[index] = { ...distance, description: e.target.value };
                              setEditForm(prev => ({ ...prev, distances: newDistances }));
                            }}
                            placeholder="Describe this distance option..."
                            rows={2}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary focus:border-primary"
                          />
                        </div>
                        {editForm.distances && editForm.distances.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newDistances = editForm.distances?.filter((_, i) => i !== index) || [];
                              setEditForm(prev => ({ ...prev, distances: newDistances }));
                            }}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                            Remove Distance
                          </button>
                        )}
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={() => {
                        const newDistance: DistanceOption = {
                          id: `distance-${Date.now()}`,
                          name: '',
                          length: '',
                          price: '',
                          description: '',
                          ageRestriction: '12+',
                          difficulty: 'Intermediate'
                        };
                        setEditForm(prev => ({ 
                          ...prev, 
                          distances: [...(prev.distances || []), newDistance] 
                        }));
                      }}
                      className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Add Distance Option
                    </button>
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-primary" />
                    Amenities
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {commonAmenities.map((amenity) => (
                      <label key={amenity} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.amenities.includes(amenity)}
                          onChange={() => handleEditAmenityToggle(amenity)}
                          className="mr-2 text-primary focus:ring-primary"
                        />
                        <span className="text-sm">{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50">
              <div className="flex space-x-3">
                <button 
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={createLoading}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUpdateEvent}
                  disabled={createLoading}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Update Event
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav className="app-bottom-nav">
        <div className="flex justify-around">
          <Link to="/dashboard" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faChartLine} className="text-lg" />
            <span className="text-xs mt-1">Dashboard</span>
          </Link>
          <Link to="/coordinator/events" className="app-bottom-nav-link-active py-2 px-3">
            <FontAwesomeIcon icon={faCalendarDays} className="text-lg" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/events" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faEye} className="text-lg" />
            <span className="text-xs mt-1">Browse</span>
          </Link>
          <Link to="/profile" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faUsers} className="text-lg" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default CoordinatorEventManagement; 